import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Dumbbell, Code, Music, MoreHorizontal, Video, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LucideIcon } from 'lucide-react';

const subjectOptions = [
  { id: 'english', labelEn: 'English', labelAr: 'إنجليزي', icon: BookOpen, color: 'bg-blue-500' },
  { id: 'fitness', labelEn: 'Fitness', labelAr: 'لياقة', icon: Dumbbell, color: 'bg-green-500' },
  { id: 'programming', labelEn: 'Programming', labelAr: 'برمجة', icon: Code, color: 'bg-orange-500' },
  { id: 'music', labelEn: 'Music', labelAr: 'موسيقى', icon: Music, color: 'bg-pink-500' },
  { id: 'other', labelEn: 'Other', labelAr: 'أخرى', icon: MoreHorizontal, color: 'bg-gray-500' },
];

const teachingStyleOptions = [
  { id: 'chat_video', labelEn: 'Chat & Video', labelAr: 'محادثة وفيديو', icon: Video, color: 'bg-purple-500' },
  { id: 'chat_only', labelEn: 'Chat Only', labelAr: 'محادثة فقط', icon: MessageSquare, color: 'bg-indigo-500' },
];

const languageOptions = [
  { id: 'arabic', labelEn: 'Arabic', labelAr: 'عربي', color: 'bg-emerald-500' },
  { id: 'english', labelEn: 'English', labelAr: 'إنجليزي', color: 'bg-sky-500' },
];

const gradeLevelOptions = [
  { id: 'preschool', labelEn: 'Preschool (3-5)', labelAr: 'ما قبل المدرسة', color: 'bg-yellow-500' },
  { id: 'elementary', labelEn: 'Elementary (K-5)', labelAr: 'ابتدائي', color: 'bg-lime-500' },
  { id: 'middle', labelEn: 'Middle School (6-8)', labelAr: 'متوسط', color: 'bg-teal-500' },
  { id: 'high', labelEn: 'High School (9-12)', labelAr: 'ثانوي', color: 'bg-blue-500' },
  { id: 'college', labelEn: 'College/University', labelAr: 'جامعة', color: 'bg-violet-500' },
  { id: 'adult', labelEn: 'Adult Learners', labelAr: 'بالغين', color: 'bg-rose-500' },
  { id: 'professional', labelEn: 'Professional Dev.', labelAr: 'تطوير مهني', color: 'bg-amber-500' },
  { id: 'all', labelEn: 'All Ages', labelAr: 'جميع الأعمار', color: 'bg-gray-500' },
];

const availabilityOptions = [
  { id: 'sunday', labelEn: 'Sunday', labelAr: 'الأحد', color: 'bg-red-400' },
  { id: 'monday', labelEn: 'Monday', labelAr: 'الإثنين', color: 'bg-orange-400' },
  { id: 'tuesday', labelEn: 'Tuesday', labelAr: 'الثلاثاء', color: 'bg-yellow-400' },
  { id: 'wednesday', labelEn: 'Wednesday', labelAr: 'الأربعاء', color: 'bg-green-400' },
  { id: 'thursday', labelEn: 'Thursday', labelAr: 'الخميس', color: 'bg-teal-400' },
  { id: 'friday', labelEn: 'Friday', labelAr: 'الجمعة', color: 'bg-blue-400' },
  { id: 'saturday', labelEn: 'Saturday', labelAr: 'السبت', color: 'bg-purple-400' },
];

const priceOptions = [
  { id: '150-500', labelEn: '150 – 500 EGP', labelAr: '$3 – $10', color: 'bg-green-500' },
  { id: '500-1000', labelEn: '500 – 1000 EGP', labelAr: '$10 – $20', color: 'bg-teal-500' },
  { id: '1000-1500', labelEn: '1000 – 1500 EGP', labelAr: '$20 – $30', color: 'bg-blue-500' },
  { id: '1500-2000', labelEn: '1500 – 2000 EGP', labelAr: '$30 – $40', color: 'bg-purple-500' },
  { id: '2000+', labelEn: '2000+ EGP', labelAr: '$40+', color: 'bg-pink-500' },
];

interface FilterOptionProps {
  label: string;
  labelAr: string;
  icon?: LucideIcon;
  color: string;
  isSelected: boolean;
  onClick: () => void;
}

const FilterOption = ({ label, labelAr, icon: Icon, color, isSelected, onClick }: FilterOptionProps) => (
  <button
    onClick={onClick}
    style={{ WebkitTapHighlightColor: 'transparent' }}
    className={`relative flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200 outline-none focus:outline-none ${
      isSelected
        ? 'border-purple-500 bg-purple-50 shadow-md scale-[1.02]'
        : 'border-transparent bg-card hover:bg-muted/50 hover:border-muted-foreground/20'
    }`}
  >
    {Icon && (
      <div className={`flex items-center justify-center w-7 h-7 rounded-md ${color}`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
    )}
    {!Icon && (
      <div className={`w-3 h-3 rounded-full ${color}`} />
    )}
    <div className="text-left">
      <span className={`block text-xs font-semibold ${isSelected ? 'text-purple-700' : 'text-foreground'}`}>
        {label}
      </span>
      <span className={`block text-[10px] ${isSelected ? 'text-purple-500' : 'text-muted-foreground'}`}>
        {labelAr}
      </span>
    </div>
    {isSelected && (
      <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    )}
  </button>
);

const steps = [
  { id: 0, titleEn: 'What do you want to learn?', titleAr: 'ماذا تريد أن تتعلم؟' },
  { id: 1, titleEn: 'Teaching Style', titleAr: 'أسلوب التعليم' },
  { id: 2, titleEn: 'Teaching Language', titleAr: 'لغة التدريس' },
  { id: 3, titleEn: 'Grade Level', titleAr: 'المرحلة الدراسية' },
  { id: 4, titleEn: 'Price Range', titleAr: 'نطاق السعر' },
  { id: 5, titleEn: 'Availability', titleAr: 'الأيام المتاحة' },
];

export const LearnFilterCard = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState<string | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<string | null>(null);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);

  const handleSearch = () => {
    // Build query params from selected filters
    const params = new URLSearchParams();
    
    // Map subject selection to search query
    if (selectedSubject) {
      const subject = subjectOptions.find(s => s.id === selectedSubject);
      if (subject) {
        params.set('subject', subject.labelEn);
      }
    }
    
    // Add other filters as search params
    if (selectedStyle) params.set('style', selectedStyle);
    if (selectedLanguage) params.set('language', selectedLanguage);
    if (selectedGradeLevel) params.set('grade', selectedGradeLevel);
    if (selectedPrice) params.set('price', selectedPrice);
    if (selectedAvailability.length > 0) params.set('availability', selectedAvailability.join(','));
    params.set('scrollToResults', 'true');
    
    // Navigate to tutors page with filters
    setIsModalOpen(false);
    navigate(`/tutors?${params.toString()}`);
  };

  const handleSelect = (step: number, value: string) => {
    switch (step) {
      case 0:
        setSelectedSubject(value);
        break;
      case 1:
        setSelectedStyle(value);
        break;
      case 2:
        setSelectedLanguage(value);
        break;
      case 3:
        setSelectedGradeLevel(value);
        break;
      case 4:
        setSelectedPrice(value);
        break;
      case 5:
        // Multi-select for availability
        setSelectedAvailability(prev => 
          prev.includes(value) 
            ? prev.filter(v => v !== value)
            : [...prev, value]
        );
        return; // Don't auto-advance for multi-select
    }
    // Auto-advance to next step after selection (except for multi-select)
    if (step < steps.length - 1) {
      setTimeout(() => setCurrentStep(step + 1), 300);
    }
  };

  const getSelectedValue = (step: number): string | null => {
    switch (step) {
      case 0: return selectedSubject;
      case 1: return selectedStyle;
      case 2: return selectedLanguage;
      case 3: return selectedGradeLevel;
      case 4: return selectedPrice;
      default: return null;
    }
  };

  const isSelectedMulti = (step: number, id: string): boolean => {
    if (step === 5) {
      return selectedAvailability.includes(id);
    }
    return false;
  };

  const getOptions = (step: number) => {
    switch (step) {
      case 0: return subjectOptions;
      case 1: return teachingStyleOptions;
      case 2: return languageOptions;
      case 3: return gradeLevelOptions;
      case 4: return priceOptions;
      case 5: return availabilityOptions;
      default: return [];
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (open) {
      // Reset all selections when modal opens
      setCurrentStep(0);
      setSelectedSubject(null);
      setSelectedStyle(null);
      setSelectedLanguage(null);
      setSelectedGradeLevel(null);
      setSelectedPrice(null);
      setSelectedAvailability([]);
    } else {
      setCurrentStep(0);
    }
  };

  const currentStepData = steps[currentStep];
  const options = getOptions(currentStep);
  const selectedValue = getSelectedValue(currentStep);

  return (
    <>
      <div 
        onClick={() => setIsModalOpen(true)}
        className="relative flex flex-col items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-50 to-pink-50 cursor-pointer transition-all duration-300 hover:shadow-lg overflow-hidden rounded-lg group border-2 border-purple-300 hover:border-purple-500 hover:scale-105"
      >
        <Search className="h-4 w-4 text-purple-600 mb-1 group-hover:scale-110 transition-transform" />
        <span className="text-[8px] font-semibold text-purple-700 text-center leading-tight">Get started</span>
      </div>

      <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 shrink-0 ${currentStep === 0 ? 'invisible' : ''}`}
                onClick={handleBack}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <DialogTitle className="text-center flex-1">
                <span className="block text-base font-bold text-purple-600">
                  Find Your Perfect Tutor
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">اعثر على معلمك المثالي</span>
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 shrink-0 ${currentStep === steps.length - 1 ? 'invisible' : ''}`}
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={currentStep === steps.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          {/* Progress indicator */}
          <div className="flex gap-1 justify-center mb-2">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStep
                    ? 'w-6 bg-purple-500'
                    : idx < currentStep
                    ? 'w-3 bg-purple-300'
                    : 'w-3 bg-muted'
                }`}
              />
            ))}
          </div>
          
          {/* Step content with animation */}
          <div className="relative min-h-[200px]">
            <div
              key={currentStep}
              className="animate-fade-in space-y-3"
            >
              <div className="text-center mb-4">
                <h3 className="text-sm font-bold text-foreground">{currentStepData.titleEn}</h3>
                <p className="text-xs text-muted-foreground">{currentStepData.titleAr}</p>
              </div>

              <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                {options.map((option) => {
                  const IconComponent = 'icon' in option ? (option as { icon: LucideIcon }).icon : undefined;
                  const isSelected = currentStep === 5 
                    ? isSelectedMulti(currentStep, option.id) 
                    : selectedValue === option.id;
                  return (
                    <FilterOption
                      key={option.id}
                      label={option.labelEn}
                      labelAr={option.labelAr}
                      icon={IconComponent}
                      color={option.color}
                      isSelected={isSelected}
                      onClick={() => handleSelect(currentStep, option.id)}
                    />
                  );
                })}
              </div>

              {/* Search button for last step */}
              {currentStep === 5 && (
                <div className="pt-3 space-y-2">
                  {selectedAvailability.length > 0 && (
                    <p className="text-xs text-center text-muted-foreground">
                      {selectedAvailability.length} day(s) selected
                    </p>
                  )}
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={handleSearch}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Search Tutors | البحث عن معلمين
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Step counter */}
          <div className="text-center text-xs text-muted-foreground pt-2">
            Step {currentStep + 1} of {steps.length}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
